// Claude: SellerProfileDrawer — buyer views seller public profile + all assets 01-Apr-2026
import React, { useEffect, useState } from "react";
import {
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerHeader,
  DrawerBody,
  DrawerCloseButton,
  Box,
  Text,
  Flex,
  Spinner,
} from "@chakra-ui/react";
import { Modal } from "antd";
import { RfxAPI } from "../../Apis/rfxAPI";
import { CommonAPI } from "../../Apis/commonApi";
import NotesModal from "../../Components/Modals/NotesModal";

interface SellerProfileDrawerProps {
  sellerId: string | null;
  companyName: string;
  isOpen: boolean;
  onClose: () => void;
}

const SellerProfileDrawer = ({ sellerId, companyName, isOpen, onClose }: SellerProfileDrawerProps) => {
  const [data, setData] = useState<{ seller: any; assets: any[] } | null>(null);
  const [loading, setLoading] = useState(false);
  const [notesOpen, setNotesOpen] = useState(false);
  // Claude: existing notes text — fetched on open; refreshed after modal closes 01-Apr-2026
  const [notesBody, setNotesBody] = useState<string>("");

  const fetchNotes = (id: string) => {
    CommonAPI.getNotes({ forUser: id })
      .then((res: any) => setNotesBody(res?.data?.data?.body ?? ""))
      .catch(() => setNotesBody(""));
  };

  useEffect(() => {
    // Claude: fetch profile + notes on open; reset on close 01-Apr-2026
    if (isOpen && sellerId) {
      setLoading(true);
      setData(null);
      setNotesBody("");
      RfxAPI.getSellerPublicProfile(sellerId)
        .then((res: any) => setData(res?.data ?? res))
        .catch(() => setData(null))
        .finally(() => setLoading(false));
      fetchNotes(sellerId);
    }
  }, [isOpen, sellerId]);

  return (
    <>
      <Drawer isOpen={isOpen} onClose={onClose} placement="right" size="md">
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerHeader fontSize="md" fontWeight={700} pb={1} px={4}>
            {companyName}
          </DrawerHeader>
          <DrawerBody overflowY="auto" px={4} pb={6}>
            {loading ? (
              <Flex justifyContent="center" py={10}>
                <Spinner size="sm" color="#0E122F" />
              </Flex>
            ) : !data ? (
              <Text fontSize="sm" color="gray.400" textAlign="center" py={10}>Could not load profile.</Text>
            ) : (
              <>
                {/* Claude: seller company info — no verified badge per design 01-Apr-2026 */}
                {data.seller.companyHqCountry && (
                  <Text fontSize="12px" color="gray.500" mb={1}>{data.seller.companyHqCountry}</Text>
                )}
                {data.seller.companyUrl && (
                  <Text
                    fontSize="12px" color="#1565C0" textDecoration="underline" cursor="pointer" mb={2}
                    onClick={() => window.open(data.seller.companyUrl, "_blank")}
                  >
                    {data.seller.companyUrl}
                  </Text>
                )}
                {data.seller.companyInfo && (
                  <Box mb={4}>
                    <Text fontSize="11px" fontWeight={600} color="gray.500" mb={1}>About</Text>
                    <Text fontSize="13px" color="gray.700" lineHeight="1.5">{data.seller.companyInfo}</Text>
                  </Box>
                )}
                {/* Claude: Company Type badges 14-04-2026 */}
                {data.seller.sellerCompanyType?.length > 0 && (
                  <Box mb={4}>
                    <Text fontSize="11px" fontWeight={600} color="gray.500" mb={1}>Company Type</Text>
                    <Flex gap={1} flexWrap="wrap">
                      {data.seller.sellerCompanyType.map((t: string, i: number) => (
                        <span key={i} style={{ padding: "2px 8px", backgroundColor: "#E3F2FD", color: "#1565C0", borderRadius: "3px", fontSize: "11px" }}>
                          {t === "Others" && data.seller.sellerCompanyTypeOther ? data.seller.sellerCompanyTypeOther : t}
                        </span>
                      ))}
                    </Flex>
                  </Box>
                )}

                {/* Claude: notes — show body if exists; "Add Notes" link if empty 01-Apr-2026 */}
                <Box mb={4}>
                  {notesBody ? (
                    <>
                      <Text fontSize="11px" fontWeight={600} color="gray.500" mb={1}>Notes</Text>
                      <Text fontSize="13px" color="gray.700" lineHeight="1.5">{notesBody}</Text>
                    </>
                  ) : (
                    <Text
                      fontSize="12px" color="#1565C0" textDecoration="underline" cursor="pointer"
                      onClick={() => setNotesOpen(true)}
                    >
                      Add Notes
                    </Text>
                  )}
                </Box>

                {/* Claude: all seller assets as list 01-Apr-2026 */}
                <Text fontSize="12px" fontWeight={600} color="gray.600" mb={2}>
                  Digital Assets ({data.assets.length})
                </Text>
                {data.assets.length === 0 ? (
                  <Text fontSize="sm" color="gray.400">No assets listed.</Text>
                ) : (
                  <Flex direction="column" gap={3}>
                    {data.assets.map((asset: any) => (
                      <Box
                        key={asset._id}
                        p={3} bg="gray.50" borderRadius="10px"
                        border="1px solid" borderColor="gray.200"
                        cursor="pointer"
                        _hover={{ bg: "#F1F8E9", borderColor: "#A5D6A7" }}
                        onClick={() => window.open(asset.assetUrl, "_blank")}
                      >
                        <Text fontSize="13px" fontWeight={600} color="#0E122F" lineHeight="1.4">
                          {asset.assetTitle}
                        </Text>
                        {asset.assetType && (
                          <Text fontSize="10px" color="#2E7D32" fontWeight={500} mt={0.5}>
                            {asset.assetType}
                          </Text>
                        )}
                        {asset.assetSummary && (
                          <Text fontSize="11px" color="gray.500" mt={1} noOfLines={2}>
                            {asset.assetSummary}
                          </Text>
                        )}
                      </Box>
                    ))}
                  </Flex>
                )}
              </>
            )}
          </DrawerBody>
        </DrawerContent>
      </Drawer>

      {/* Claude: Notes modal — same as seller list; forUser=sellerId; no eventId (platform not event-centric) 01-Apr-2026 */}
      {/* Claude: zIndex 1500 — must exceed Chakra Drawer (~1400) to appear on top 01-Apr-2026 */}
      {/* Claude: re-fetch notes on modal close so inline display updates 01-Apr-2026 */}
      <Modal closeIcon={null} footer={null} title={null} open={notesOpen}
        onOk={() => { setNotesOpen(false); if (sellerId) fetchNotes(sellerId); }}
        onCancel={() => { setNotesOpen(false); if (sellerId) fetchNotes(sellerId); }}
        zIndex={1500}>
        <NotesModal
          forUser={sellerId ?? ""}
          companyName={companyName}
          handleCloseModal={() => setNotesOpen(false)}
        />
      </Modal>
    </>
  );
};

export default SellerProfileDrawer;
